import * as THREE from '../../libs/three.js/build/three.module.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { Utils } from '../utils.js';
import {
  Surface,
  nextSurfaceColor,
  scenePointToOffsetFrame,
  createMeasureMarkerMaterial,
  MEASURE_MARKER_GEOMETRY,
  MEASURE_CLOUD_MARKER_COLOR,
  scaleMeasureMarker,
  canAddSurface,
  surfaceLimitMessage,
  surfaceIndexBlockers,
  surfaceIndexMissingMessage,
} from './Surface.js';
import { bindViewSurfaces, growRestoredSurfaces } from '../viewer/ViewState.js';

export class SurfaceTool extends EventDispatcher {
  constructor(viewer) {
    super();
    this.viewer = viewer;
    this.active = false;
    this._preview = null;
    this._lastHit = null;
    this._spinners = new Map();
    this._growTail = Promise.resolve();
    this._activeMouseButton = null;
    this._onPreviewDrag = this._onPreviewDrag.bind(this);
    this._onPreviewDrop = this._onPreviewDrop.bind(this);
    this._onCancel = this._onCancel.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);

    viewer.addEventListener('update', () => {
      const cam = viewer.scene.getActiveCamera();
      for (const s of viewer.scene.surfaces || []) {
        if (s.pointcloud) s._updateOverlay(viewer);
        s.updateScale(cam, viewer.renderer);
      }
      this._syncSceneSpinners();
      if (this.active) {
        this._syncPreviewFromMouse();
        this._scalePreview();
      }
    });
    const bindOverlays = () => {
      bindViewSurfaces(viewer);
      growRestoredSurfaces(viewer);
      this._syncSceneSpinners();
    };
    viewer.scene.addEventListener('pointcloud_added', bindOverlays);
    viewer.addEventListener('scene_changed', (e) => {
      e.oldScene?.removeEventListener('pointcloud_added', bindOverlays);
      e.scene?.addEventListener('pointcloud_added', bindOverlays);
      bindOverlays();
    });
  }

  startInsertion() {
    if (!canAddSurface(this.viewer.scene)) {
      this.viewer.postError?.(surfaceLimitMessage());
      return;
    }
    // Same gate as the dimmed toolbar button (its click still fires).
    const blockers = surfaceIndexBlockers(this.viewer.scene);
    if (blockers.length > 0) {
      this.viewer.postError?.(surfaceIndexMissingMessage(blockers));
      return;
    }
    this.viewer.dispatchEvent({ type: 'cancel_insertions' });
    this.active = true;
    this.viewer.measureModeEnabled = true;
    if (this.viewer.inputHandler) this.viewer.inputHandler.measureModeEnabled = true;
    this._ensurePreview();
    this.viewer.addEventListener('cancel_insertions', this._onCancel);
    document.addEventListener('keydown', this._onKeyDown, true);
    this.viewer.renderer?.domElement?.addEventListener('mousedown', this._onPointerDown, true);
    this.viewer.inputHandler?.startDragging(this._preview);
    this._syncPreviewFromMouse();
  }

  stopInsertion() {
    if (!this.active) return;
    this.active = false;
    this.viewer.measureModeEnabled = false;
    if (this.viewer.inputHandler) {
      this.viewer.inputHandler.measureModeEnabled = false;
      if (this.viewer.inputHandler.drag?.object === this._preview) {
        this.viewer.inputHandler.startDragging(null);
      }
    }
    this.viewer.removeEventListener('cancel_insertions', this._onCancel);
    document.removeEventListener('keydown', this._onKeyDown, true);
    this.viewer.renderer?.domElement?.removeEventListener('mousedown', this._onPointerDown, true);
    this._hidePreview();
    // Same signal as finished measurements: drop the toolbar highlight.
    this.viewer.dispatchEvent({ type: 'cancel_insertions' });
  }

  _onCancel() {
    this.stopInsertion();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') {
      this.stopInsertion();
    }
  }

  /** Right mouse button cancels the insertion, same as Escape; tracked so
   * _onPreviewDrop can tell a genuine left-click confirm from any other
   * button's mouseup (InputHandler dispatches 'drop' on release regardless
   * of which button was pressed). */
  _onPointerDown(e) {
    this._activeMouseButton = e.button;
    if (e.button === THREE.MOUSE.RIGHT) {
      this.stopInsertion();
    }
  }

  _overlayScene() {
    return this.viewer.measuringTool?.scene || null;
  }

  _ensurePreview() {
    if (this._preview) {
      this._overlayScene()?.add(this._preview);
      return;
    }
    this._preview = new THREE.Mesh(
      MEASURE_MARKER_GEOMETRY,
      createMeasureMarkerMaterial(MEASURE_CLOUD_MARKER_COLOR)
    );
    this._preview.name = 'surface-candidate';
    this._preview.visible = false;
    this._preview.addEventListener('drag', this._onPreviewDrag);
    this._preview.addEventListener('drop', this._onPreviewDrop);
    this._overlayScene()?.add(this._preview);
  }

  _hidePreview() {
    this._lastHit = null;
    if (!this._preview) return;
    this._preview.visible = false;
    this._overlayScene()?.remove(this._preview);
  }

  _scalePreview() {
    if (!this._preview?.visible) return;
    scaleMeasureMarker(this._preview, this.viewer.scene.getActiveCamera(), this.viewer.renderer);
  }

  _syncPreviewFromMouse() {
    const mouse = this.viewer.inputHandler?.mouse;
    if (mouse) this._applyPreviewPick(mouse);
  }

  _onPreviewDrag(e) {
    const mouse = e.drag?.end || this.viewer.inputHandler?.mouse;
    if (mouse) this._applyPreviewPick(mouse);
  }

  _applyPreviewPick(mouse) {
    if (!this._preview) return;
    const I = Utils.getMousePointCloudIntersection(
      mouse,
      this.viewer.scene.getActiveCamera(),
      this.viewer,
      this.viewer.scene.pointclouds,
      { pickClipped: true }
    );
    if (!I?.location || !I.pointcloud) {
      this._preview.visible = false;
      this._lastHit = null;
      return;
    }
    this._preview.position.copy(I.location);
    this._preview.visible = true;
    this._lastHit = I;
    this._scalePreview();
  }

  _onPreviewDrop() {
    if (!this.active) return;
    if (this._activeMouseButton !== THREE.MOUSE.LEFT) {
      // Non-left release (right-click already cancelled via _onPointerDown;
      // this covers e.g. middle-click) must not confirm the point.
      this.viewer.inputHandler?.startDragging(this._preview);
      return;
    }
    const I = this._lastHit;
    if (!I?.location || !I.pointcloud) {
      const msg =
        (typeof i18n !== 'undefined' && i18n.t)
          ? i18n.t('surfaces.no_hit', 'Click a point cloud to detect a surface.')
          : 'Click a point cloud to detect a surface.';
      this.viewer.postError?.(msg);
      this.viewer.inputHandler?.startDragging(this._preview);
      return;
    }
    this._commitHit(I);
  }

  _commitHit(I) {
    this.stopInsertion();
    const pc = I.pointcloud;
    const cloudUuid = pc.noachCloudUuid || null;
    if (!cloudUuid) {
      const msg =
        (typeof i18n !== 'undefined' && i18n.t)
          ? i18n.t('surfaces.grow_unavailable', 'Surface detection is not connected.')
          : 'Surface detection is not connected.';
      this.viewer.postError?.(msg);
      return;
    }
    // A cloud shown after insertion started may still lack the sidecar.
    if (pc.noachSurfaceReady !== true) {
      this.viewer.postError?.(surfaceIndexMissingMessage([pc]));
      return;
    }
    const seedOffset = scenePointToOffsetFrame(pc, I.location);
    if (![seedOffset.x, seedOffset.y, seedOffset.z].every(Number.isFinite)) {
      const msg =
        (typeof i18n !== 'undefined' && i18n.t)
          ? i18n.t('surfaces.no_hit', 'Click a point cloud to detect a surface.')
          : 'Click a point cloud to detect a surface.';
      this.viewer.postError?.(msg);
      return;
    }
    if (!canAddSurface(this.viewer.scene)) {
      this.viewer.postError?.(surfaceLimitMessage());
      return;
    }
    const surface = new Surface({
      pointcloud: pc,
      cloudUuid: pc.noachCloudUuid || null,
      scanKey: pc.noachScanKey ?? null,
      seedOffset,
      color: nextSurfaceColor(this.viewer.scene.surfaces),
      name:
        (typeof i18n !== 'undefined' && i18n.t)
          ? i18n.t('measurements.name_surface', 'Surface')
          : 'Surface',
    });
    this.attach(surface);
    if (!this.viewer.scene.addSurface(surface)) {
      this.viewer.postError?.(surfaceLimitMessage());
      return;
    }
    this.viewer.scene.selectedSurface = surface;
    surface._updateOverlay(this.viewer);
    this.grow(surface);
  }

  /** Wire seed-drag → grow. Safe to call more than once on the same surface. */
  attach(surface) {
    if (!surface || surface._toolAttached) return surface;
    surface._toolAttached = true;
    surface.addEventListener('seed_moved', () => this.grow(surface));
    return surface;
  }

  grow(surface) {
    const fn = this.viewer.noachGrowSurface;
    if (typeof fn !== 'function') {
      surface.error = 'grow_unavailable';
      surface._restoreGrown = false;
      const msg =
        (typeof i18n !== 'undefined' && i18n.t)
          ? i18n.t('surfaces.grow_unavailable', 'Surface detection is not connected.')
          : 'Surface detection is not connected.';
      this.viewer.postError?.(msg);
      return Promise.resolve();
    }
    if (surface._growInFlight) {
      surface._growQueued = true;
      return surface._growPromise || Promise.resolve();
    }
    surface.busy = true;
    surface._growInFlight = true;
    surface._growQueued = false;
    this._syncSceneSpinners();
    surface.dispatchEvent({ type: 'changed', surface });
    const run = () => {
      const request = {
        seedAtStart: [surface.seedOffset.x, surface.seedOffset.y, surface.seedOffset.z],
        paramsAtStart: {
          angleDeg: surface.angleDeg,
          maxDistance: surface.maxDistance,
          maxGap: surface.maxGap,
        },
      };
      return Promise.resolve(
        fn({
          cloudUuid: surface.cloudUuid,
          scanKey: surface.scanKey,
          point: request.seedAtStart.slice(),
          angleDeg: request.paramsAtStart.angleDeg,
          maxDistance: request.paramsAtStart.maxDistance,
          maxGap: request.paramsAtStart.maxGap,
        })
      )
        .then((result) => surface.applyGrowResult(result, this.viewer, request))
        .catch((err) => {
          surface.error = err;
          surface._restoreGrown = false;
          // The host may attach a translated, reason-specific message
          // (e.g. "the source point is on an edge"); it beats the generic one.
          const hostMsg = err && typeof err.userMessage === 'string' ? err.userMessage.trim() : '';
          const msg =
            hostMsg ||
            ((typeof i18n !== 'undefined' && i18n.t)
              ? i18n.t('surfaces.grow_failed', 'Surface detection failed.')
              : 'Surface detection failed.');
          this.viewer.postError?.(msg);
          this.viewer.dispatchEvent({ type: 'surface_grow_failed', surface, error: err });
        });
    };
    // One HTTP grow at a time so octree Range fetches keep a free connection.
    const queued = this._growTail.catch(() => undefined).then(run);
    this._growTail = queued.then(
      () => undefined,
      () => undefined
    );
    surface._growPromise = queued.then(() => {
      surface._growInFlight = false;
      surface._growPromise = null;
      if (surface._growQueued) {
        surface._growQueued = false;
        return this.grow(surface);
      }
      surface.busy = false;
      this._syncSceneSpinners();
      surface.dispatchEvent({ type: 'changed', surface });
    });
    return surface._growPromise;
  }

  _ensureSpinnerLayer() {
    if (typeof document === 'undefined') return null;
    if (this._spinnerLayer && this._spinnerLayer.isConnected) return this._spinnerLayer;
    const el = document.createElement('div');
    el.className = 'surface-scene-spinners';
    el.setAttribute('aria-hidden', 'true');
    const host = this.viewer.renderArea;
    if (host) host.appendChild(el);
    this._spinnerLayer = el;
    this._spinners = this._spinners || new Map();
    return el;
  }

  _syncSceneSpinners() {
    const layer = this._ensureSpinnerLayer();
    if (!layer) return;
    const camera = this.viewer.scene.getActiveCamera();
    const host = this.viewer.renderArea;
    const w = host?.clientWidth || 0;
    const h = host?.clientHeight || 0;
    const seen = new Set();
    const tmp = this._spinnerNdc || (this._spinnerNdc = new THREE.Vector3());
    const n = this._spinnerN || (this._spinnerN = new THREE.Vector3());
    for (const surface of this.viewer.scene.surfaces || []) {
      if (!surface.busy || !surface.visible || !surface.pointcloud) continue;
      seen.add(surface.uuid);
      let el = this._spinners?.get(surface.uuid);
      if (!el) {
        el = document.createElement('div');
        el.className = 'surface-scene-spinner';
        el.setAttribute('role', 'status');
        const ring = document.createElement('div');
        ring.className = 'surface-scene-spinner-ring';
        el.appendChild(ring);
        layer.appendChild(el);
        this._spinners.set(surface.uuid, el);
      }
      const hex = surface.color?.getHexString?.();
      const ring = el.querySelector('.surface-scene-spinner-ring');
      if (hex && ring) ring.style.borderTopColor = `#${hex}`;
      tmp.copy(surface.seedSphere.position);
      const dir = surface.worldNormal?.(n);
      if (dir) {
        const along = surface._arrowLen > 0 ? surface._arrowLen * 0.55 : 0.25;
        tmp.addScaledVector(dir, along);
      }
      tmp.project(camera);
      const inFront = tmp.z >= -1 && tmp.z <= 1;
      const onScreen = tmp.x >= -1.15 && tmp.x <= 1.15 && tmp.y >= -1.15 && tmp.y <= 1.15;
      if (!inFront || !onScreen || w < 1 || h < 1) {
        el.classList.remove('is-visible');
        continue;
      }
      const x = (tmp.x * 0.5 + 0.5) * w;
      const y = (-tmp.y * 0.5 + 0.5) * h;
      el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      el.classList.add('is-visible');
    }
    for (const [uuid, el] of this._spinners || []) {
      if (seen.has(uuid)) continue;
      el.remove();
      this._spinners.delete(uuid);
    }
  }
}
